import { serverInfo } from "../config/serverConfig";
import { checkResponse } from "../utils/apiError";


import { apiFetch } from "./apiFetch";

/**
 * Fetch saved page view/filter state (scoped by account + user + page key).
 * @param {string} accountId
 * @param {string} userId
 * @param {string} pageKey
 * @returns {Promise<object | null>}
 */
export async function fetchPagePreferences(accountId, userId, pageKey) {
  if (!accountId || !userId || !pageKey) return null;
  const resp = await apiFetch(serverInfo.serverUrl + "/getPagePreferences", {
    method: "POST",
    body: JSON.stringify({
      account_id: accountId,
      user_id: userId,
      page_key: pageKey,
    }),
  });
  checkResponse(resp);
  const data = await resp.json();
  if (!data || data.state == null || typeof data.state !== "object") return null;
  return data.state;
}

/**
 * Save page view/filter state as this user's default for the page (overwrites prior).
 * @param {string} accountId
 * @param {string} userId
 * @param {string} pageKey
 * @param {object} state
 */
export async function savePagePreferences(accountId, userId, pageKey, state) {
  if (!accountId || !userId || !pageKey || state == null || typeof state !== "object") {
    throw new Error("accountId, userId, pageKey and state object are required");
  }
  const resp = await apiFetch(serverInfo.serverUrl + "/savePagePreferences", {
    method: "POST",
    body: JSON.stringify({
      account_id: accountId,
      user_id: userId,
      page_key: pageKey,
      state,
    }),
  });
  checkResponse(resp);
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || "Failed to save page preferences");
  }
}

/**
 * Remove saved page view/filter state for this user and page.
 * @param {string} accountId
 * @param {string} userId
 * @param {string} pageKey
 */
export async function clearPagePreferences(accountId, userId, pageKey) {
  if (!accountId || !userId || !pageKey) {
    throw new Error("accountId, userId, and pageKey are required");
  }
  const resp = await apiFetch(serverInfo.serverUrl + "/clearPagePreferences", {
    method: "POST",
    body: JSON.stringify({
      account_id: accountId,
      user_id: userId,
      page_key: pageKey,
    }),
  });
  checkResponse(resp);
  if (!resp.ok) {
    const text = await resp.text();
    throw new Error(text || "Failed to clear page preferences");
  }
}

/** Stable page keys for saved filter/view defaults. */
export const PAGE_VIEW_PREFERENCE_KEYS = Object.freeze({
  taskList: "task.list",
  eventsList: "events.list",
  eventSignups: "events.signups",
});
